"use client";

import React, { useState, useEffect } from "react";
import { Headset, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
export default function MainNavigationHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';

        // Corrected line 34: removed the extra {
        if (href.startsWith('#') && !href.startsWith('#/')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#') && !href.includes('#/')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }

        // Only trigger smooth scroll for section IDs, not page routes
        if (hash && hash.startsWith('#') && !hash.includes('/') && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header data-section-id="6933" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", "bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-md" : "bg-background border-border shadow-none")}>
      <div className="container mx-auto px-4 h-[80px] flex items-center justify-between gap-4">
        
        <Link to="/" className="flex items-center gap-3 shrink-0 group">
          <img src="https://cobrnik.com/wp-content/uploads/2026/04/cobrnik_cavaliers_logo.png" alt="Cobrnik Cavaliers" className="h-10 w-auto object-contain transition-transform group-hover:scale-105" />
        </Link>

        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <Link to="/about-us" className="text-muted-foreground hover:text-primary transition-colors p-2 rounded-full hover:bg-accent font-sans text-sm font-medium" aria-label="About Us">
            About Us
          </Link>

          <Link to="/contact-application" className="text-muted-foreground hover:text-primary transition-colors p-2 rounded-full hover:bg-accent font-sans text-sm font-medium" aria-label="Contact Us">
            Contact Us
          </Link>

          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium px-6 hidden sm:inline-flex border-none font-sans">
            <Link to="/contact-application">Application</Link>
          </Button>

          <Button variant="ghost" size="icon" className="md:hidden text-muted-foreground hover:text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <Menu className="size-6" />
          </Button>
        </div>
      </div>

      <div className={cn("md:hidden px-4 pb-4 transition-all duration-300 overflow-hidden bg-background border-t border-border/50", isMobileMenuOpen ? "max-h-[400px] opacity-100 py-4" : "max-h-0 opacity-0 py-0")}>
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Link to="/about-us" className="flex items-center py-2 px-3 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-accent rounded-md transition-colors font-sans">
              About Us
            </Link>

            <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 justify-start rounded-full">
              <Link to="/contact-application">Contact Application</Link>
            </Button>
            
            <MenuProvider menu_id="49" className="w-full">
              <MobileMenuConsumer />
            </MenuProvider>
          </div>
        </div>
      </div>
    </header>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div className="h-10 w-full bg-muted animate-pulse rounded-md" />;
  return <nav className="flex flex-col gap-1">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="flex items-center py-2 px-3 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-accent rounded-md transition-colors font-sans">
          {item.label}
        </Link>)}
    </nav>;
}